"use client";

import { BookOpen, Play } from "lucide-react";
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useLesson, type Lesson } from "@/contexts/lesson-context";
import { LessonContentViewer } from "./lesson-content-viewer";
import { MiniLessonViewer } from "./mini-lesson-viewer";

export function InputCanvas() {
  const { currentLesson, setCurrentLesson, currentMiniLesson, setCurrentMiniLesson } = useLesson();
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [selectedMiniLesson, setSelectedMiniLesson] = useState<typeof currentMiniLesson>(null);
  const [localLessons, setLocalLessons] = useState<Lesson[]>([]);

  // Sync from context (sidebar or agent)
  useEffect(() => {
    if (currentLesson) {
      const exists = localLessons.some(l => l.id === currentLesson.id);
      if (!exists) {
        setLocalLessons(prev => [currentLesson, ...prev]);
      }
      setSelectedLesson(currentLesson);
      setSelectedMiniLesson(null);
    }
  }, [currentLesson, localLessons]);

  useEffect(() => {
    if (currentMiniLesson) {
      setSelectedMiniLesson(currentMiniLesson);
      setSelectedLesson(null);
    }
  }, [currentMiniLesson]);

  const handleLessonSelect = (lesson: Lesson) => {
    setSelectedLesson(lesson);
    setSelectedMiniLesson(null);
    setCurrentLesson(lesson);
    setCurrentMiniLesson(null);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header — brand-aligned for blue panel */}
      <div className="shrink-0 p-4 border-b border-white/20 bg-[var(--mindup-navy)]/30">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-white" />
          </div>
          <h2 className="font-display text-base font-bold text-white">Curriculum Library</h2>
        </div>
        <p className="text-xs text-white/80 ml-9">Browse and select lesson content</p>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {selectedMiniLesson ? (
          <MiniLessonViewer
            miniLesson={selectedMiniLesson}
            onBack={() => {
              setSelectedMiniLesson(null);
              setCurrentMiniLesson(null);
            }}
          />
        ) : selectedLesson ? (
          <LessonContentViewer
            lesson={selectedLesson}
            onBack={() => setSelectedLesson(null)}
          />
        ) : (
          /* Lessons List View */
          <div className="h-full overflow-y-auto space-y-3 p-3">
            {localLessons.length === 0 ? (
              <div className="text-center py-8 text-sm text-white/80">
                Select a lesson to view it here
              </div>
            ) : (
              localLessons.map((lesson) => (
                <Card
                  key={lesson.id}
                  className="cursor-pointer gap-0 hover:shadow-lg transition-all border-white/30 bg-white rounded-2xl hover:border-[var(--mindup-pink)]/50 hover:shadow-[var(--mindup-pink)]/10"
                  onClick={() => handleLessonSelect(lesson)}
                >
                  <CardHeader className="px-4 pt-4 pb-1 gap-1">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-sm font-bold mb-1">{lesson.title}</CardTitle>
                        <CardDescription className="text-xs">
                          {lesson.gradeLevel} · {lesson.duration}
                        </CardDescription>
                      </div>
                      <div className="w-7 h-7 rounded-lg bg-[var(--mindup-pink)]/10 flex items-center justify-center shrink-0">
                        <Play className="w-3.5 h-3.5 text-[var(--mindup-pink)]" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="px-4 pt-0 pb-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                      {lesson.description}
                    </p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
